import { PropertyTypes } from "../../../../enums/PropertyTypes";
import { ISchema, ISchemaChild, ISchemaProperty } from "../Schema";
import { EntityNames } from "../../../../enums/EntityNames";
import { EntityViewTypes } from "../../../../enums/EntityViewTypes";
import { hasPermission } from "../../../../modules/auth/Permissions/PermissionModule";
import { PermissionConcerns, PermissionTypes } from "../../../../modules/auth/Permissions/PermissionStrings";
import SidePanelOuterCubes from "../../layout/SidePanel/layouts/SidePanelOuterCube";
import { computeV } from "../../../../helpers/SchemaComputeFunction";

export const OuterCubeSchema: ISchema = {
    displayName: "Pallet",
    GetSidePanelComponent: (data, onViewPage) => <SidePanelOuterCubes data={data} onViewPage={onViewPage} />,
    editWarning: "Any changes will apply to all packing boxes and their contents",
    editButtonText: "Apply All Changes",
    props: [
        {
            name: 'id',
            type: PropertyTypes.string,
            key: true
        } as ISchemaProperty,
        {
            name: 'reference',
            displayName: 'Reference',
            descriptor: true,
            type: PropertyTypes.string,
            key: false,
            showInList: true,
            showInForm: false,
            showInView: false,
            requiredInForm: false,
        } as ISchemaProperty,
        {
            name: 'belongsTo',
            displayName: 'BelongsTo',
            type: PropertyTypes.string,
            key: false,
            showInList: true,
            showInForm: false,
            showInView: false,
            requiredInForm: false,
        } as ISchemaProperty,
        {
            name: 'owner',
            displayName: 'Owner',
            type: PropertyTypes.owner,
            key: false,
            showInList: false,
            showInForm: true,
            showInView: false,
            requiredInForm: true,
            entityName: EntityNames.accounts,
            addOnly: true
        } as ISchemaProperty,
        {
            name: 'resultType',
            displayName: 'Type',
            type: PropertyTypes.string,
            key: false,
            showInList: true,
            showInForm: false,
            showInView: false,
            requiredInForm: false,
        } as ISchemaProperty,
        {
            name: 'status',
            displayName: 'Status',
            type: PropertyTypes.status,
            key: false,
            showInList: true,
            showInForm: false,
            showInView: false,
            requiredInForm: false,
        } as ISchemaProperty,
        {
            name: 'currentLocation',
            displayName: 'Current Location',
            type: PropertyTypes.breadcrumb,
            key: false,
            showInList: false,
            showInForm: false,
            showInView: false,
            requiredInForm: false,
        } as ISchemaProperty,
        {
            name: 'percentageFull',
            displayName: 'Percentage Full',
            type: PropertyTypes.number,
            key: false,
            showInList: false,
            showInForm: false,
            showInView: true,
            requiredInForm: false,
        } as ISchemaProperty,
        {
            name: 'accountId',
            displayName: 'Belongs to',
            type: PropertyTypes.selectByEntityName,
            key: false,
            showInList: false,
            showInForm: true,
            showInView: false,
            requiredInForm: false,
            entityName: EntityNames.accounts,
            rememberLastFormValue: true,
            editOnly: true
        } as ISchemaProperty,
        {
            name: 'typeId',
            displayName: 'Contents type',
            type: PropertyTypes.PackingBoxType,
            key: false,
            showInList: false,
            showInForm: true,
            showInView: false,
            requiredInForm: false,
            entityName: EntityNames.caseMaterialTypes,
            rememberLastFormValue: true,
            editOnly: true
        } as ISchemaProperty,
        {
            name: 'vNumberStandardSize',
            displayName: 'Standard Size',
            type: PropertyTypes.vNumberStandardSize,
            key: false,
            showInList: false,
            showInForm: true,
            showInView: false,
            requiredInForm: false,
            className: "small-12 medium-12",
            rememberLastFormValue: false,
            editOnly: true
        } as ISchemaProperty,
        {
            name: 'width',
            displayName: 'Width (cm)',
            type: PropertyTypes.number,
            key: false,
            showInList: false,
            showInForm: false,
            showInView: false,
            requiredInForm: false,
            className: "small-12 medium-4",
            rememberLastFormValue: true,
        } as ISchemaProperty,
        {
            name: 'height',
            displayName: 'Height (cm)',
            type: PropertyTypes.number,
            key: false,
            showInList: false,
            showInForm: false,
            showInView: false,
            requiredInForm: false,
            className: "small-12 medium-4",
            rememberLastFormValue: true,
        } as ISchemaProperty,
        {
            name: 'depth',
            displayName: 'Depth (cm)',
            type: PropertyTypes.number,
            key: false,
            showInList: false,
            showInForm: false,
            showInView: false,
            requiredInForm: false,
            className: "small-12 medium-4",
            rememberLastFormValue: true,
        } as ISchemaProperty,
        {
            name: 'computedVSize',
            displayName: 'Computed V size',
            type: PropertyTypes.computed,
            key: false,
            showInList: false,
            showInForm: true,
            showInView: false,
            requiredInForm: false,
            className: "small-12 medium-3",
            computeFunction: ((x: number[]) => computeV([...x])) as Function,
            computeProperties: ["width", "height", "depth"],
            rememberLastFormValue: true,
            editOnly: true
        } as ISchemaProperty
    ],

    children: [
        { entityName: EntityNames.packingBox, displayName: "Packing Boxes", viewType: EntityViewTypes.list, enableFilters: true, canAdd: ((ctx) => hasPermission(ctx.permissions, PermissionTypes.write, PermissionConcerns.Manage_Packing_Box)), alwaysShowheader: true } as ISchemaChild,
        { entityName: EntityNames.ranges, displayName: "Ranges", viewType: EntityViewTypes.list, enableFilters: true, alwaysShowheader: true } as ISchemaChild,
        { entityName: EntityNames.cases, displayName: "Cases", viewType: EntityViewTypes.list, enableFilters: true, alwaysShowheader: true } as ISchemaChild,
        { entityName: EntityNames.specimens, displayName: "Specimens", viewType: EntityViewTypes.list, enableFilters: true, alwaysShowheader: true } as ISchemaChild,
        { entityName: EntityNames.audits, displayName: "Audit Trail", viewType: EntityViewTypes.list, cantView: true, permissionToView: ((ctx) => hasPermission(ctx.permissions, PermissionTypes.read, PermissionConcerns.Inventory_Audit_Trails)), alwaysShowheader: true } as ISchemaChild,
    ]
}
